import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { env } from './env';
import { getSupabase } from './supabase';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'planner' | 'viewer';
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  isMock: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const MOCK_USER_KEY = 'wfp-v2:mock-auth:v1';

function loadMockUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(MOCK_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = getSupabase();

  useEffect(() => {
    let mounted = true;

    if (env.isMock) {
      setUser(loadMockUser());
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    supabase!.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      const s = data.session;
      if (s?.user) {
        setUser({
          id: s.user.id,
          email: s.user.email ?? '',
          displayName: (s.user.user_metadata?.display_name as string) ?? s.user.email ?? '',
          role: ((s.user.user_metadata?.role as AuthUser['role']) ?? 'planner') as AuthUser['role'],
        });
      }
      setLoading(false);
    });

    const { data: sub } = supabase!.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (!session?.user) {
        setUser(null);
        return;
      }
      setUser({
        id: session.user.id,
        email: session.user.email ?? '',
        displayName:
          (session.user.user_metadata?.display_name as string) ?? session.user.email ?? '',
        role: ((session.user.user_metadata?.role as AuthUser['role']) ?? 'planner') as AuthUser['role'],
      });
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [supabase]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      if (env.isMock) {
        if (!env.mockAuthEmail || !env.mockAuthPassword) {
          return { error: 'Đăng nhập demo bị tắt ở môi trường này. Cần cấu hình Supabase.' };
        }
        const okEmail = email.trim().toLowerCase() === env.mockAuthEmail.toLowerCase();
        const okPw = password === env.mockAuthPassword;
        if (!okEmail || !okPw) {
          return { error: 'Email hoặc mật khẩu không đúng (mock mode)' };
        }
        const mockUser: AuthUser = {
          id: 'mock-user-id',
          email,
          displayName: 'Demo Planner',
          role: 'admin',
        };
        window.localStorage.setItem(MOCK_USER_KEY, JSON.stringify(mockUser));
        setUser(mockUser);
        return { error: null };
      }
      const { error } = await supabase!.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    if (env.isMock) {
      window.localStorage.removeItem(MOCK_USER_KEY);
      setUser(null);
      return;
    }
    await supabase!.auth.signOut();
  }, [supabase]);

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signOut, isMock: env.isMock }),
    [user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
